import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloseIcon from "@mui/icons-material/Close";

// Preset prompts offered in the Regenerate-Answer dialog. The backend will
// eventually provide these via an endpoint; until then this is the single
// source of truth shared between AddCategory and QuestionBankView.
// Keep "Custom..." LAST — the dialog branches on the sentinel `__custom__`.
export const REGEN_PROMPT_PRESETS = [
  { label: "Make the answer more concise", value: "Make the answer more concise" },
  { label: "Provide a more detailed explanation", value: "Provide a more detailed explanation, including examples where helpful" },
  { label: "Use simpler language", value: "Rewrite the answer in simpler language suitable for beginners" },
  { label: "Add a worked example", value: "Add a worked example to the answer" },
  { label: "Improve accuracy", value: "Re-check the answer for technical accuracy and correct any errors" },
  { label: "Custom...", value: "__custom__" },
];

const commonTextFieldStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "white" },
    "&:hover fieldset": { borderColor: "#d1d1d1" },
    "&.Mui-focused fieldset": { borderColor: "#1c97e3" },
  },
};

/**
 * Reusable dialog for picking a prompt before calling /RegenerateAnswers.
 *
 * Owns its preset+custom-text state internally so callers don't have to plumb
 * it through. Resolves the selection to a single `prompt` string (or null when
 * nothing is chosen → backend uses its default prompt) and hands it to
 * `onConfirm`. The parent owns the API call and toggles `loading`.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {(prompt: string | null) => void} onConfirm
 * @param {boolean} loading - disables actions + blocks close during the call
 * @param {React.ReactNode} targetSummary - optional preview (question text, "3 selected", etc.)
 * @param {string} title - default "Regenerate Answer"
 * @param {string} subtitle - default "Choose a preset prompt or enter your own instructions."
 * @param {string} confirmLabel - default "Regenerate"
 */
const RegenerateAnswerDialog = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  targetSummary = null,
  title = "Regenerate Answer",
  subtitle = "Write your instructions, or pick a preset to start from.",
  confirmLabel = "Regenerate",
}) => {
  const [preset, setPreset] = useState(null);
  const [promptText, setPromptText] = useState("");

  // Reset every time the dialog re-opens so the previous prompt doesn't
  // leak across invocations.
  useEffect(() => {
    if (open) {
      setPreset(null);
      setPromptText("");
    }
  }, [open]);

  const handleClose = () => {
    if (!loading) onClose();
  };

  // The textarea is the single source of truth — preset selections just
  // pre-fill it (the trainer can still edit afterwards). Send null when
  // empty so the backend uses its default prompt.
  const handleConfirm = () => {
    const trimmed = (promptText || "").trim();
    onConfirm(trimmed || null);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ backgroundColor: "#1c97e3", p: 0.6, borderRadius: "8px", display: "flex" }}>
              <AutorenewIcon sx={{ color: "white", fontSize: "22px" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#1c2434" }}>
                {title}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#8E9EBC", fontWeight: 500 }}>
                {subtitle}
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={handleClose} disabled={loading}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {targetSummary && (
          <Box
            sx={{
              backgroundColor: "#f3f4f6",
              borderRadius: "10px",
              p: 2,
              mb: 2.5,
              fontSize: "13px",
              color: "#555",
              maxHeight: "140px",
              overflowY: "auto",
            }}
          >
            {targetSummary}
          </Box>
        )}

        {/* Quick-fill preset dropdown — selecting a preset pre-populates the
            textarea below. The textarea remains the single source of truth. */}
        <Typography sx={{ fontWeight: 600, fontSize: "13px", color: "#64748b", mb: 1 }}>
          Quick presets (optional)
        </Typography>
        <Autocomplete
          size="small"
          options={REGEN_PROMPT_PRESETS}
          getOptionLabel={(option) => option?.label || ""}
          isOptionEqualToValue={(o, v) => o?.value === v?.value}
          value={preset}
          disabled={loading}
          onChange={(_, newValue) => {
            setPreset(newValue);
            if (!newValue) return;
            if (newValue.value === "__custom__") {
              // "Custom..." starts the trainer with a blank slate.
              setPromptText("");
            } else {
              // Pre-fill the textarea with the preset text — trainer can edit.
              setPromptText(newValue.value);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Pick a preset to start from"
              sx={{ ...commonTextFieldStyle, backgroundColor: "#f8f9fa", borderRadius: "8px" }}
            />
          )}
        />

        {/* Always-visible prompt textarea — type freely; leave blank to use
            the backend's default prompt. */}
        <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#1c2434", mt: 2.5, mb: 1 }}>
          Prompt
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={4}
          disabled={loading}
          placeholder="Enter your instructions for answer generation… (leave blank to use the default prompt)"
          value={promptText}
          onChange={(e) => setPromptText(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: "#f9fafb",
            },
          }}
        />

        {!promptText.trim() && (
          <Typography sx={{ fontSize: "12px", color: "#94a3b8", mt: 1 }}>
            Empty prompt — backend will use its default.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ textTransform: "none", fontWeight: 600, color: "#555" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={loading}
          onClick={handleConfirm}
          sx={{ textTransform: "none", borderRadius: "10px", px: 3, fontWeight: 600, display: "flex", gap: 1 }}
        >
          {loading ? (
            <CircularProgress size={16} sx={{ color: "white" }} />
          ) : (
            <AutorenewIcon fontSize="small" />
          )}
          {loading ? "Regenerating…" : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegenerateAnswerDialog;
